import { useMemo } from 'react';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatCurrency(amount) {
  return '$' + Math.round(amount).toLocaleString('en-US');
}

function calcOutstanding(job) {
  const paid = (job.payments ?? []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  return Math.max(0, (Number(job.contractedAmount) || 0) - paid);
}

// ─── sub-components ─────────────────────────────────────────────────────────

function StatPill({ color, value, label }) {
  const dotStyle = { backgroundColor: color };
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gray-50 dark:bg-[#13202E] border border-gray-200 dark:border-[#243348]">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={dotStyle} />
      <span className="text-[var(--wm-text-caption)] font-bold tabular-nums" style={{ color }}>
        {value}
      </span>
      <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] font-medium">
        {label}
      </span>
    </div>
  );
}

function PersonIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>
  );
}

function AvatarCircle({ name, color }) {
  const initials = name
    ? name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('')
    : '?';
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: color || '#2E8BF0' }}
    >
      <span className="text-[var(--wm-text-caption)] font-bold text-white leading-none">
        {initials}
      </span>
    </div>
  );
}

// ─── main widget ────────────────────────────────────────────────────────────

const MAX_ROWS = 6;

export default function SubcontractorSnapshotWidget() {
  const data = useMemo(() => {
    try {
      const rawSubs = localStorage.getItem('wm-subcontractors-v1');
      const rawJobs = localStorage.getItem('wm-sub-jobs-v1');

      const subs = JSON.parse(rawSubs || '[]');
      const jobs = JSON.parse(rawJobs || '[]');

      if (!Array.isArray(subs) || !Array.isArray(jobs)) {
        return { hasData: false, activeJobCount: 0, totalOutstanding: 0, rows: [] };
      }

      // Filter to active jobs only
      const activeJobs = jobs.filter((j) => j.status === 'active');

      if (activeJobs.length === 0) {
        return { hasData: true, activeJobCount: 0, totalOutstanding: 0, rows: [] };
      }

      const activeJobCount = activeJobs.length;

      // Total outstanding across all active jobs
      const totalOutstanding = activeJobs.reduce((sum, j) => sum + calcOutstanding(j), 0);

      // Build per-sub aggregates
      const subMap = new Map();
      for (const sub of subs) {
        subMap.set(sub.id, sub);
      }

      const subAggMap = new Map();
      for (const job of activeJobs) {
        const sub = subMap.get(job.subId);
        if (!sub) continue;
        if (!subAggMap.has(job.subId)) {
          subAggMap.set(job.subId, {
            sub,
            jobCount: 0,
            outstanding: 0,
          });
        }
        const agg = subAggMap.get(job.subId);
        agg.jobCount += 1;
        agg.outstanding += calcOutstanding(job);
      }

      // Sort by outstanding desc, take max 6
      const rows = Array.from(subAggMap.values())
        .sort((a, b) => b.outstanding - a.outstanding)
        .slice(0, MAX_ROWS);

      return { hasData: true, activeJobCount, totalOutstanding, rows };
    } catch {
      return { hasData: false, activeJobCount: 0, totalOutstanding: 0, rows: [] };
    }
  }, []);

  const { activeJobCount, totalOutstanding, rows } = data;

  // ── empty state ──────────────────────────────────────────────────────────
  if (!data.hasData || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#13202E] flex items-center justify-center text-[#94A3B8] dark:text-[#3D5470]">
          <PersonIcon />
        </div>
        <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">
          No active subcontractor jobs right now.
        </p>
      </div>
    );
  }

  // ── normal state ─────────────────────────────────────────────────────────
  const outstandingColor = totalOutstanding > 0 ? '#f59e0b' : '#64748B';

  return (
    <div className="space-y-3">

      {/* Summary pills */}
      <div className="flex flex-wrap items-center gap-2">
        <StatPill color="#2E8BF0" value={activeJobCount} label="Active Jobs" />
        <StatPill
          color={outstandingColor}
          value={formatCurrency(totalOutstanding)}
          label="Outstanding"
        />
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 dark:border-[#243348]" />

      {/* Sub rows */}
      <div className="-mx-4">
        {rows.map(({ sub, jobCount, outstanding }, idx) => {
          const isLast = idx === rows.length - 1;
          const isActive = sub.status === 'active';
          const statusDotColor = isActive ? '#22c55e' : '#94A3B8';
          const balanceColor = outstanding > 0 ? '#f59e0b' : '#94A3B8';

          return (
            <div
              key={sub.id}
              className={[
                'flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors',
                !isLast ? 'border-b border-gray-100 dark:border-[#1D2D40]' : '',
              ].join(' ')}
            >
              {/* Avatar */}
              <AvatarCircle name={sub.name} color={sub.avatarColor} />

              {/* Name + specialty */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] truncate leading-tight">
                    {sub.name}
                  </span>
                  {sub.specialty && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[var(--wm-text-caption)] font-medium bg-gray-100 dark:bg-[#243348] text-[#64748B] dark:text-[#7D93AE] border-gray-200 dark:border-[#2F4560] flex-shrink-0">
                      {sub.specialty}
                    </span>
                  )}
                </div>
                <p className="text-[var(--wm-text-meta)] text-[#7D93AE] leading-tight mt-0.5">
                  {jobCount} active {jobCount === 1 ? 'job' : 'jobs'}
                </p>
              </div>

              {/* Outstanding balance */}
              <span
                className="text-[var(--wm-text-body)] font-semibold tabular-nums flex-shrink-0"
                style={{ color: balanceColor }}
              >
                {formatCurrency(outstanding)}
              </span>

              {/* Status dot */}
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: statusDotColor }}
                title={sub.status}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
